import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useFeatureGate } from "@statsig/react-bindings";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";

import { useChat } from "../../context/ChatContext";
import { ChatContent } from "./ChatContent";
import { ChatInput, Filter } from "./ChatInput";
import { EmptyChat, LoadingChat } from "./ChatPlaceholders";
import { ModelSelect } from "./ModelSelect";
import { StatusAlert } from "./StatusAlert";

export const Chat = () => {
  const theme = useTheme();
  const location = useLocation();
  const { chatId } = useParams<{ chatId: string }>();

  const { chatTheme } = theme;

  // Ref for scrollable container
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Filter state management
  const [filters, setFilters] = useState<Filter[]>([]);

  const handleDeleteFilter = useCallback((filterId: string) => {
    setFilters((prev) => prev.filter((f) => f.id !== filterId));
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setFilters([]);
  }, []);

  // Extract question from URL query parameters
  const searchParams = new URLSearchParams(location.search);
  const prefillQuestion = searchParams.get("question");

  const {
    chatMessages,
    isLoadingMessages,
    isLoadingMetadata,
    metadata,
    currentQuestion,
    currentPreSetConstraints,
    askQuestion,
    isAsking,
    isStreaming,
    todoItems,
    setCurrentStreamId,
  } = useChat();

  useEffect(() => {
    if (chatId) {
      setCurrentStreamId(chatId);
    }
  }, [chatId, setCurrentStreamId]);

  const { value: isWarcFeatureEnabled } = useFeatureGate(featureFlag.enableWarc);

  // WARC is enabled if both the feature flag is on AND the user has premium tier
  const warcEnabledForTier = isWarcFeatureEnabled && metadata?.hendrenTier === "PREMIUM";

  // Auto-submit prefilled question from URL query parameter
  useEffect(() => {
    const queryLimitReached = metadata?.queryLimitReached || false;

    if (prefillQuestion && !queryLimitReached && !chatId) {
      askQuestion(prefillQuestion, warcEnabledForTier);
    }
  }, [metadata]);

  // Auto-scroll to bottom when a new question is asked
  useEffect(() => {
    if (currentQuestion && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [currentQuestion]);

  if (isLoadingMessages || isLoadingMetadata) {
    return <LoadingChat />;
  }

  const queryLimitReached = metadata?.queryLimitReached || false;
  const remainingQueries = metadata?.remainingQueries || 0;
  const isFreeUser = metadata?.hendrenTier === "FREE";

  if (!currentQuestion && !isStreaming && chatMessages.length === 0) {
    return (
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        alignItems="stretch"
        p={{ xs: 1.5, sm: 2.5 }}
        height="100%"
        sx={{
          maxWidth: "100%",
          overflow: "hidden",
        }}
      >
        <Box
          flex={1}
          mb={2}
          display="flex"
          flexDirection="column"
          minHeight={0}
          sx={{
            overflowY: "auto",
            px: { xs: 0, sm: 1 },
          }}
        >
          <EmptyChat onAskQuestion={askQuestion} warcEnabledForTier={warcEnabledForTier} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      flex={1}
      display="flex"
      flexDirection="column"
      alignItems="stretch"
      height="100%"
      sx={{
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Box
        ref={scrollContainerRef}
        sx={{
          px: { xs: 1.5, sm: 2.5 },
          pt: { xs: 1.5, sm: 2.5 },
          pb: 0,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        <Stack direction="row" gap={2} alignItems="center" sx={{ mb: 2 }}>
          <ModelSelect />
        </Stack>
        <Box
          sx={{
            px: { xs: 0, sm: 1 },
            pb: 2,
            maxWidth: "1000px",
            width: "100%",
            alignSelf: { xs: "stretch", lg: "center" },
          }}
        >
          <ChatContent
            chatMessages={chatMessages}
            currentQuestion={currentQuestion}
            currentPreSetConstraints={currentPreSetConstraints}
            isStreaming={isStreaming}
            todoItems={todoItems}
          />
        </Box>
      </Box>

      <Box
        component="section"
        sx={{
          position: "sticky",
          bottom: 0,
          backgroundColor: "background.paper !important",
          pb: { xs: 1.5, sm: 2.5 },
          pt: 2,
          zIndex: 3,
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 -20px 25px -5px #343434, 0 -10px 10px -5px #343434"
              : "0 -20px 25px -5px rgba(255, 255, 255, 1), 0 -10px 10px -5px rgba(255, 255, 255, 0.8)",
        }}
      >
        {/* Query limit alerts */}
        {!queryLimitReached && isFreeUser && (
          <StatusAlert
            title={`You have ${remainingQueries} MAITE ${remainingQueries === 1 ? "query" : "queries"} remaining`}
            severity="info"
            sx={{ mb: chatTheme.spacing.statusMargin }}
          >
            Talk to your Marketing Science Partner to unlock full MAITE access. Get unlimited
            queries, chat history, and much more.
          </StatusAlert>
        )}
        {queryLimitReached && (
          <StatusAlert
            title="You've used all your free queries for this month"
            severity="warning"
            sx={{ mb: chatTheme.spacing.statusMargin }}
          >
            Talk to your Marketing Science Partner to unlock full MAITE access. Get unlimited
            queries, chat history, and much more.
          </StatusAlert>
        )}

        <ChatInput
          filters={filters}
          isAnswerPending={isAsking}
          warcEnabledForTier={warcEnabledForTier}
          disabled={queryLimitReached}
          onAskQuestion={askQuestion}
          onDeleteFilter={handleDeleteFilter}
          onClearAllFilters={handleClearAllFilters}
        />
        {/* Disclaimer */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: chatTheme.spacing.componentMargin, display: "block", textAlign: "center" }}
        >
          MAITE can make mistakes. Consider checking important information.
        </Typography>
      </Box>
    </Box>
  );
};
